export const UNDERSTANDING_DOCS = {
  BUDGETING: {
    title: "Budgets",
    // These feed your static SegmentMenu buttons perfectly
    navigation: [
      {
        path: "help_center",
        text: "Help Center",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
      {
        path: "faq",
        text: "FAQ",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
    ],
    meta: {
      help_center: { layoutType: "list" },
      faq: { layoutType: "accordion" },
    },
    content: {
      help_center: [
        {
          id: "b-hc-1",
          title: "Understanding Budgeting",
          bodyContent: [
            {
              type: "section",
              heading: "How budgeting works in Plaintr",
              children: [
                {
                  type: "paragraph",
                  text: "Budgeting isn’t about restricting your spending. It’s about understanding it and getting better at it over time.",
                },
                {
                  type: "paragraph",
                  text: "Here’s how it works.",
                },
                {
                  type: "list",
                  items: [
                    "Where your money consistently goes",
                    "Where you tend to go over",
                    "What stays stable",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "What a budget actually helps you understand",
              children: [
                {
                  type: "paragraph",
                  text: "A budget isn’t just a way to limit your spending.",
                },
                {
                  type: "paragraph",
                  text: "It’s a way to understand it.",
                },
                {
                  type: "paragraph",
                  text: "When you set a budget, you’re not trying to control every dollar. You’re creating a reference point — something you can compare your behavior against.",
                },
                {
                  type: "paragraph",
                  text: "Over time, that helps you see patterns like:",
                },
                {
                  type: "list",
                  items: [
                    "where your money consistently goes",
                    "where you tend to go over",
                    "what stays stable",
                    "what changes from one cycle to the next",
                  ],
                },
                {
                  type: "paragraph",
                  text: "That’s the real value.",
                },
                {
                  type: "paragraph",
                  text: "Not the number itself, but what it reveals.",
                },
              ],
            },
            {
              type: "section",
              heading: "How budgeting connects to your financial progress",
              children: [
                {
                  type: "paragraph",
                  text: "Budgeting is where your financial progress starts.",
                },
                {
                  type: "paragraph",
                  text: "Every budget you create is a way to measure how you’re doing over time.",
                },
                {
                  type: "paragraph",
                  text: "When you set a budget, you’re not trying to control every dollar. You’re creating a reference point — something you can compare your behavior against.",
                },
                {
                  type: "paragraph",
                  text: "When you stay within your limit, that tells you something. When you go over, that tells you something too.",
                },
                {
                  type: "paragraph",
                  text: "What matters is not a single week or month.",
                },
                {
                  type: "paragraph",
                  text: "It’s the pattern.",
                },
                {
                  type: "paragraph",
                  text: "Over time, your budgets help you answer:",
                },
                {
                  type: "list",
                  items: [
                    "Am I becoming more consistent?",
                    "Am I improving in certain areas?",
                    "Where am I still struggling?",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This is how budgeting connects to your overall progress.",
                },
                {
                  type: "paragraph",
                  text: "It turns everyday spending into something you can learn from — not just react to.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why budgets reset (and why that matters)",
              children: [
                {
                  type: "paragraph",
                  text: "Budgets reset because behavior happens in cycles.",
                },
                {
                  type: "paragraph",
                  text: "You don’t spend money once — you spend it over time.",
                },
                {
                  type: "paragraph",
                  text: "That’s why each budget runs on a weekly or monthly cycle.",
                },
                {
                  type: "paragraph",
                  text: "At the end of that cycle:",
                },
                {
                  type: "list",
                  items: [
                    "your budget resets",
                    "your progress for that period is saved",
                    "a new cycle begins",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This matters because it gives you:",
                },
                {
                  type: "list",
                  items: [
                    "a clean starting point",
                    "a consistent way to measure behavior",
                    "a way to compare one period to another",
                  ],
                },
                {
                  type: "paragraph",
                  text: "Without resets, everything would blur together.",
                },
                {
                  type: "paragraph",
                  text: "With them, you can actually see change.",
                },
              ],
            },
            {
              type: "section",
              heading: "What budgeting really does",
              children: [
                {
                  type: "paragraph",
                  text: "At the end of the day, budgeting in Plaintr isn’t about numbers.",
                },
                {
                  type: "paragraph",
                  text: "It’s about patterns.",
                },
                {
                  type: "paragraph",
                  text: "It helps you see:",
                },
                {
                  type: "list",
                  items: [
                    "what you’re doing",
                    "how it’s changing",
                    "what that might mean",
                    "and what you might want to adjust next",
                  ],
                },
                {
                  type: "paragraph",
                  text: "So instead of guessing, you’re making decisions with context.",
                },
              ],
            },
          ],
        },
        {
          id: "b-hc-2",
          title: "Setting Up Your Budget",
          bodyContent: [
            {
              type: "section",
              heading: "You set a limit for something that matters",
              children: [
                {
                  type: "paragraph",
                  text: "You choose what you want to track — like dining, groceries, or anything else — and set a spending limit for a period of time.",
                },
                {
                  type: "paragraph",
                  text: "That limit becomes your reference point.",
                },
                {
                  type: "paragraph",
                  text: "Not to judge you — but to help you see how you’re doing.",
                },
              ],
            },
            {
              type: "section",
              heading: "You can make it as simple or as specific as you want",
              children: [
                {
                  type: "paragraph",
                  text: "Some people like to track one category — like dining or groceries.",
                },
                {
                  type: "paragraph",
                  text: "Others want to go deeper — like limiting spending from a specific account or across everything.",
                },
                {
                  type: "paragraph",
                  text: "You can do both.",
                },
                {
                  type: "paragraph",
                  text: "Your budget can be:",
                },
                {
                  type: "list",
                  items: [
                    "focused on a specific type of spending",
                    "tied to where the money is coming from",
                    "or broad enough to cover everything",
                  ],
                },
                {
                  type: "paragraph",
                  text: "A budget can include one or multiple categories, accounts, or institutions — depending on how you set it up.",
                },
                {
                  type: "paragraph",
                  text: "The goal isn’t to fit into a system.",
                },
                {
                  type: "paragraph",
                  text: "It’s to match how you actually manage your money.",
                },
              ],
            },
            {
              type: "section",
              heading: "We track it automatically",
              children: [
                {
                  type: "paragraph",
                  text: "Once your budget is set, everything updates in real time.",
                },
                {
                  type: "paragraph",
                  text: "As new transactions come in, your budget adjusts — so you always know:",
                },
                {
                  type: "list",
                  items: [
                    "how much you’ve spent",
                    "how much you have left",
                    "how close you are to your limit",
                  ],
                },
                {
                  type: "paragraph",
                  text: "No manual tracking needed.",
                },
              ],
            },
            {
              type: "section",
              heading: "You’ll know when you’re getting close",
              children: [
                {
                  type: "paragraph",
                  text: "You can set a threshold — for example, 75% of your budget.",
                },
                {
                  type: "paragraph",
                  text: "When you reach it, we’ll let you know.",
                },
                {
                  type: "paragraph",
                  text: "Not as a warning — just a signal.",
                },
                {
                  type: "paragraph",
                  text: "You’re getting close. Your pace is changing. This is your moment to check in.",
                },
                {
                  type: "paragraph",
                  text: "In some cases, this signal can also reflect how your spending is trending — not just how close you are to your limit.",
                },
              ],
            },
            {
              type: "section",
              heading: "Each cycle resets — but nothing is lost",
              children: [
                {
                  type: "paragraph",
                  text: "Your budget resets every week or month, depending on what you chose.",
                },
                {
                  type: "paragraph",
                  text: "But your history stays.",
                },
                {
                  type: "paragraph",
                  text: "That means you can look back and see:",
                },
                {
                  type: "list",
                  items: [
                    "how consistent you’ve been",
                    "where you tend to go over",
                    "where you’re improving",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "You can adjust anytime",
              children: [
                {
                  type: "paragraph",
                  text: "Your spending changes. Your budget can too.",
                },
                {
                  type: "paragraph",
                  text: "You can update, pause, or remove a budget whenever you need.",
                },
                {
                  type: "paragraph",
                  text: "No pressure. No lock-in.",
                },
                {
                  type: "paragraph",
                  text: "Just a system that adapts to your real life.",
                },
                {
                  type: "paragraph",
                  text: "Some settings — like the cycle — can’t be changed after creation, so your history stays consistent over time.",
                },
              ],
            },
          ],
        },
        {
          id: "b-hc-3",
          title: "Understanding Your Budget in Action",
          bodyContent: [
            {
              type: "section",
              heading: "What counts toward your budget",
              children: [
                {
                  type: "paragraph",
                  text: "Your budget is based on the transactions that match what you chose to track.",
                },
                {
                  type: "paragraph",
                  text: "That usually means spending — not income.",
                },
                {
                  type: "paragraph",
                  text: "Your budget doesn’t track everything. It tracks what you told it to.",
                },
                {
                  type: "paragraph",
                  text: "A budget can include one or multiple categories, accounts, or institutions — depending on how it’s set up.",
                },
              ],
            },
            {
              type: "section",
              heading: "If something looks off",
              children: [
                {
                  type: "paragraph",
                  text: "Sometimes your total spending and your budget don’t match exactly.",
                },
                {
                  type: "paragraph",
                  text: "That’s usually because:",
                },
                {
                  type: "list",
                  items: [
                    "not every transaction is included",
                    "some transactions fall outside your setup",
                    "categories may be assigned differently than expected",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This isn’t an error.",
                },
                {
                  type: "paragraph",
                  text: "It’s just how your budget is defined.",
                },
              ],
            },
            {
              type: "section",
              heading: "How your budget updates in real time",
              children: [
                {
                  type: "paragraph",
                  text: "Your budget updates automatically as new transactions come in.",
                },
                {
                  type: "paragraph",
                  text: "There’s nothing you need to refresh or track manually.",
                },
                {
                  type: "paragraph",
                  text: "At any moment, you can see:",
                },
                {
                  type: "list",
                  items: [
                    "how much you’ve spent",
                    "how much you have left",
                    "how close you are to your limit",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "What “at risk” means",
              children: [
                {
                  type: "paragraph",
                  text: "“At risk” doesn’t mean you did something wrong.",
                },
                {
                  type: "paragraph",
                  text: "It means you’re getting close to your limit.",
                },
                {
                  type: "paragraph",
                  text: "Think of it as a signal.",
                },
                {
                  type: "paragraph",
                  text: "Not a warning — just context.",
                },
                {
                  type: "paragraph",
                  text: "In some cases, it can also reflect how your spending is trending — not just how close you are to your limit.",
                },
              ],
            },
            {
              type: "section",
              heading: "What happens when you go over your budget",
              children: [
                {
                  type: "paragraph",
                  text: "Going over your budget isn’t a failure.",
                },
                {
                  type: "paragraph",
                  text: "It’s information.",
                },
                {
                  type: "paragraph",
                  text: "It shows you how your spending compared to your plan.",
                },
                {
                  type: "paragraph",
                  text: "That result is saved — and becomes part of your pattern.",
                },
              ],
            },
          ],
        },
        {
          id: "b-hc-4",
          title: "Tracking Your Progress",
          bodyContent: [
            {
              type: "section",
              heading: "How budget history works",
              children: [
                {
                  type: "paragraph",
                  text: "Every time your budget completes a cycle, the result is saved.",
                },
                {
                  type: "paragraph",
                  text: "Nothing gets overwritten.",
                },
                {
                  type: "paragraph",
                  text: "Each week or month becomes a data point you can look back on.",
                },
                {
                  type: "paragraph",
                  text: "Over time, those data points become patterns.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to tell if you’re improving",
              children: [
                {
                  type: "paragraph",
                  text: "Improvement doesn’t come from one “perfect” cycle.",
                },
                {
                  type: "paragraph",
                  text: "You’re likely improving if:",
                },
                {
                  type: "list",
                  items: [
                    "spending stays closer to your limit",
                    "patterns become more consistent",
                    "large swings happen less often",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "Why consistency matters more than one good cycle",
              children: [
                {
                  type: "paragraph",
                  text: "A single good cycle is helpful.",
                },
                {
                  type: "paragraph",
                  text: "But consistency is what actually changes your financial behavior.",
                },
                {
                  type: "paragraph",
                  text: "Progress isn’t about perfection.",
                },
                {
                  type: "paragraph",
                  text: "It’s about patterns becoming more stable over time.",
                },
              ],
            },
          ],
        },
        {
          id: "b-hc-5",
          title: "Managing Your Budget",
          bodyContent: [
            {
              type: "section",
              heading: "How to update a budget",
              children: [
                {
                  type: "paragraph",
                  text: "You can update your budget at any time.",
                },
                {
                  type: "paragraph",
                  text: "This isn’t starting over.",
                },
                {
                  type: "paragraph",
                  text: "It’s adjusting your reference point.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to pause or restart a budget",
              children: [
                {
                  type: "paragraph",
                  text: "Pausing stops tracking — but keeps your history.",
                },
                {
                  type: "paragraph",
                  text: "Restarting picks things back up.",
                },
                {
                  type: "paragraph",
                  text: "Nothing is lost.",
                },
                {
                  type: "paragraph",
                  text: "When you restart, the system reconnects your activity from where it left off, so your history stays continuous.",
                },
              ],
            },
            {
              type: "section",
              heading: "What happens when you delete a budget",
              children: [
                {
                  type: "paragraph",
                  text: "Deleting removes the budget and its history.",
                },
                {
                  type: "paragraph",
                  text: "Once it’s gone, that data is no longer available.",
                },
              ],
            },
            {
              type: "section",
              heading: "A quick distinction",
              children: [
                {
                  type: "paragraph",
                  text: "Pausing keeps your history. Deleting removes it.",
                },
              ],
            },
          ],
        },
      ],
      faq: [
        {
          title: "Why did my budget reset?",
          description:
            "Budgets reset based on the cycle you chose — weekly, by-weekly or monthly. This gives you a clean starting point and a consistent way to measure behavior over time.",
        },
        {
          title: "Why doesn’t my budget match my total spending?",
          description:
            "Your budget only includes what you chose to track. If something looks different, it’s usually because certain transactions fall outside that setup.",
        },
        {
          title: "Can I have more than one budget?",
          description:
            "Yes. You can track different areas of your spending separately. Each one helps you understand a different part of your behavior.",
        },
        {
          title: "Can I change my budget later?",
          description:
            "Yes. You can update your limit, what’s included, or your threshold at any time. Some settings — like the cycle — can’t be changed after creation.",
        },
        {
          title: "Why can’t I change the budget cycle?",
          description:
            "The cycle defines how your behavior is measured over time. To keep your history consistent, it can’t be changed after creation.",
        },
        {
          title: "What happens if I delete a budget?",
          description:
            "The budget and its history are permanently removed. If you might need that information later, consider pausing instead.",
        },
        {
          title: "What does “at risk” mean?",
          description:
            "It means you’re approaching your limit. It’s a signal — not a warning. It can also reflect how your spending is trending over time.",
        },
        {
          title: "Is going over my budget a bad thing?",
          description:
            "No. It’s information. What matters is whether it happens once — or becomes a pattern over time.",
        },
      ],
    },
  },
  TRANSACTIONS: {
    title: "Transactions",
    // These feed your static SegmentMenu buttons perfectly
    navigation: [
      {
        path: "help_center",
        text: "Help Center",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
      {
        path: "faq",
        text: "FAQ",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
    ],
    meta: {
      help_center: { layoutType: "list" },
      faq: { layoutType: "accordion" },
    },
    content: {
      help_center: [
        {
          id: "t-hc-1",
          title: "Understanding Transactions",
          bodyContent: [
            {
              type: "section",
              heading: "How transactions work in Plaintr",
              children: [
                {
                  type: "paragraph",
                  text: "Transactions are the foundation of everything you see in Plaintr.",
                },
                {
                  type: "paragraph",
                  text: "They show what actually happened — your real financial activity.",
                },
                {
                  type: "paragraph",
                  text: "Everything else builds on top of that.",
                },
                {
                  type: "paragraph",
                  text: "Budgets, insights, and progress all depend on this data.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why transactions matter",
              children: [
                {
                  type: "paragraph",
                  text: "Transactions aren’t just a list of activities.",
                },
                {
                  type: "paragraph",
                  text: "They’re what allow you to understand your financial behavior.",
                },
                {
                  type: "paragraph",
                  text: "They help you see:",
                },
                {
                  type: "list",
                  items: [
                    "where your money is going",
                    "how your spending changes over time",
                    "what patterns repeat",
                  ],
                },
                {
                  type: "paragraph",
                  text: "Without transactions, there’s no context.",
                },
                {
                  type: "paragraph",
                  text: "With them, everything becomes measurable.",
                },
              ],
            },
            {
              type: "section",
              heading: "How transactions connect to your budgets and progress",
              children: [
                {
                  type: "paragraph",
                  text: "Every budget you create is based on your transactions.",
                },
                {
                  type: "paragraph",
                  text: "Every pattern you see comes from your transactions.",
                },
                {
                  type: "paragraph",
                  text: "Every improvement over time is measured using them.",
                },
                {
                  type: "paragraph",
                  text: "They are the starting point for:",
                },
                {
                  type: "list",
                  items: [
                    "tracking your spending",
                    "identifying recurring behavior",
                    "measuring consistency",
                    "understanding change over time",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "What your transaction history shows (and what it doesn’t)",
              children: [
                {
                  type: "paragraph",
                  text: "Your transaction history shows your financial activity over time.",
                },
                {
                  type: "paragraph",
                  text: "In Plaintr, you can see up to 24 months of transactions.",
                },
                {
                  type: "paragraph",
                  text: "This gives you enough context to understand patterns and compare different periods.",
                },
                {
                  type: "paragraph",
                  text: "What it doesn’t show is anything outside your connected accounts.",
                },
                {
                  type: "paragraph",
                  text: "If something isn’t connected, it won’t appear here.",
                },
              ],
            },
          ],
        },
        {
          id: "t-hc-2",
          title: "Viewing & Filtering Transactions",
          bodyContent: [
            {
              type: "section",
              heading: "How to filter your transactions",
              children: [
                {
                  type: "paragraph",
                  text: "Your full transaction history can be a lot to look at.",
                },
                {
                  type: "paragraph",
                  text: "Filtering helps you focus on what matters.",
                },
                {
                  type: "paragraph",
                  text: "You can filter your transactions based on:",
                },
                {
                  type: "list",
                  items: [
                    "institution",
                    "account",
                    "merchant",
                    "date range",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This allows you to narrow your view and explore a specific behavior or pattern.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to search for a specific transaction",
              children: [
                {
                  type: "paragraph",
                  text: "If you’re looking for something specific, you don’t have to scroll through everything.",
                },
                {
                  type: "paragraph",
                  text: "You can search or filter by merchant or date to find it quickly.",
                },
                {
                  type: "paragraph",
                  text: "This is useful when:",
                },
                {
                  type: "list",
                  items: [
                    "reviewing a specific charge",
                    "checking past activity",
                    "looking for patterns tied to one merchant",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "How to focus on a specific time period",
              children: [
                {
                  type: "paragraph",
                  text: "You can set a start and end date to view transactions within a specific timeframe.",
                },
                {
                  type: "paragraph",
                  text: "If you only set a start date, the system will show everything from that point until today.",
                },
                {
                  type: "paragraph",
                  text: "This helps you:",
                },
                {
                  type: "list",
                  items: [
                    "review recent activity",
                    "compare time periods",
                    "focus on specific events or changes",
                  ],
                },
              ],
            },
          ],
        },
        {
          id: "t-hc-3",
          title: "Understanding Your Transaction Data",
          bodyContent: [
            {
              type: "section",
              heading: "Why some transactions take time to appear",
              children: [
                {
                  type: "paragraph",
                  text: "Transactions don’t always appear instantly.",
                },
                {
                  type: "paragraph",
                  text: "In some cases, there’s a delay between when a transaction happens and when it shows up in Plaintr.",
                },
                {
                  type: "paragraph",
                  text: "This depends on how frequently your financial institution updates its data.",
                },
                {
                  type: "paragraph",
                  text: "Plaintr reflects that timing.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why your transactions may look different than expected",
              children: [
                {
                  type: "paragraph",
                  text: "Sometimes a transaction may not look exactly how you expect.",
                },
                {
                  type: "paragraph",
                  text: "This can happen because:",
                },
                {
                  type: "list",
                  items: [
                    "the merchant name appears differently",
                    "the category may not match how you think about it",
                    "the transaction is still pending or updating",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This is normal and part of how financial data is processed.",
                },
              ],
            },
            {
              type: "section",
              heading: "How transactions are categorized",
              children: [
                {
                  type: "paragraph",
                  text: "Transactions are automatically categorized based on available data.",
                },
                {
                  type: "paragraph",
                  text: "This helps organize your activity into meaningful groups.",
                },
                {
                  type: "paragraph",
                  text: "These categories are what power:",
                },
                {
                  type: "list",
                  items: [
                    "your budgets",
                    "your insights",
                    "your progress tracking",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "Why transaction details can change",
              children: [
                {
                  type: "paragraph",
                  text: "Some transactions may update after they first appear.",
                },
                {
                  type: "paragraph",
                  text: "For example:",
                },
                {
                  type: "list",
                  items: [
                    "a pending transaction becomes finalized",
                    "the merchant name is clarified",
                    "the category is adjusted",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This is part of how financial data stabilizes over time.",
                },
              ],
            },
          ],
        },
        {
          id: "t-hc-4",
          title: "Managing Transactions",
          bodyContent: [
            {
              type: "section",
              heading: "How to view transaction details",
              children: [
                {
                  type: "paragraph",
                  text: "Each transaction includes its own details.",
                },
                {
                  type: "paragraph",
                  text: "You can open any transaction to see more information about it.",
                },
                {
                  type: "paragraph",
                  text: "This helps you understand exactly what happened.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to add a note to a transaction",
              children: [
                {
                  type: "paragraph",
                  text: "You can add a note to any transaction.",
                },
                {
                  type: "paragraph",
                  text: "This is useful for adding context — like what the purchase was for.",
                },
                {
                  type: "paragraph",
                  text: "Notes only apply to that specific transaction.",
                },
                {
                  type: "paragraph",
                  text: "They don’t carry over to similar or recurring transactions.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to mark a transaction as tax-deductible",
              children: [
                {
                  type: "paragraph",
                  text: "You can mark a transaction as tax-deductible.",
                },
                {
                  type: "paragraph",
                  text: "This allows you to flag it for future reference.",
                },
                {
                  type: "paragraph",
                  text: "This setting applies only to that individual transaction.",
                },
              ],
            },
            {
              type: "section",
              heading: "What you can and cannot change",
              children: [
                {
                  type: "paragraph",
                  text: "You can:",
                },
                {
                  type: "list",
                  items: [
                    "add notes",
                    "mark transactions as tax-deductible",
                  ],
                },
                {
                  type: "paragraph",
                  text: "You cannot:",
                },
                {
                  type: "list",
                  items: [
                    "change the transaction amount",
                    "change the original source data",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This ensures your data remains accurate and consistent.",
                },
              ],
            },
          ],
        },
        {
          id: "t-hc-5",
          title: "Data Limitations & Expectations",
          bodyContent: [
            {
              type: "section",
              heading: "How far back your transaction history goes",
              children: [
                {
                  type: "paragraph",
                  text: "Plaintr provides up to 24 months of transaction history.",
                },
                {
                  type: "paragraph",
                  text: "This gives you a strong view of your financial patterns over time.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why data may be delayed",
              children: [
                {
                  type: "paragraph",
                  text: "Transaction data depends on your connected financial institutions.",
                },
                {
                  type: "paragraph",
                  text: "Some institutions update more frequently than others.",
                },
                {
                  type: "paragraph",
                  text: "This can cause slight delays in what you see.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why updates are not always instant",
              children: [
                {
                  type: "paragraph",
                  text: "Plaintr updates your data as it becomes available.",
                },
                {
                  type: "paragraph",
                  text: "In the current version, there is no manual refresh.",
                },
                {
                  type: "paragraph",
                  text: "Your data updates automatically based on your connection.",
                },
              ],
            },
            {
              type: "section",
              heading: "What affects how your data updates",
              children: [
                {
                  type: "paragraph",
                  text: "Your data depends on:",
                },
                {
                  type: "list",
                  items: [
                    "your connected accounts",
                    "how often your institution updates",
                    "when transactions are finalized",
                  ],
                },
                {
                  type: "paragraph",
                  text: "Plaintr reflects what’s available — as soon as it’s available.",
                },
              ],
            },
          ],
        },
      ],
      faq: [
        {
          title: "Why is my transaction missing?",
          description:
            "It may not have been reported yet by your financial institution. Some transactions take time to appear.",
        },
        {
          title: "Why did a transaction show up late?",
          description:
            "Some transactions are delayed depending on how your institution processes data. This is normal.",
        },
        {
          title: "Can I edit a transaction?",
          description:
            "You can add notes or mark it as tax-deductible. You can’t change the original transaction details.",
        },
        {
          title: "Why does my transaction look different from my bank?",
          description:
            "Sometimes merchant names or categories appear differently. This can happen during processing or categorization.",
        },
        {
          title: "Can I refresh my transactions?",
          description:
            "Not manually. Your transactions update automatically as new data becomes available.",
        },
        {
          title: "Why are only 24 months available?",
          description:
            "That’s the current history window supported by the system. It provides enough data to understand patterns over time.",
        },
        {
          title: "Why don’t my changes apply to similar transactions?",
          description:
            "Each transaction is treated individually. Changes only apply to the specific transaction you edited.",
        },
      ],
    },
  },
  SPENDINGS: {
    title: "Spendings",
    // These feed your static SegmentMenu buttons perfectly
    navigation: [
      {
        path: "help_center",
        text: "Help Center",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
      {
        path: "faq",
        text: "FAQ",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
    ],
    meta: {
      help_center: { layoutType: "list" },
      faq: { layoutType: "accordion" },
    },
    content: {
      help_center: [
        {
          id: "s-hc-1",
          title: "Understanding Spendings",
          bodyContent: [
            {
              type: "section",
              heading: "How spendings work in Plaintr",
              children: [
                {
                  type: "paragraph",
                  text: "Spendings are an analytical view of your financial activity.",
                },
                {
                  type: "paragraph",
                  text: "They show how much you spent, where it went, and how it changed over time.",
                },
                {
                  type: "paragraph",
                  text: "Unlike budgets, spendings are not something you create or manage.",
                },
                {
                  type: "paragraph",
                  text: "They are calculated based on your transactions.",
                },
              ],
            },
            {
              type: "section",
              heading: "What counts as spending",
              children: [
                {
                  type: "paragraph",
                  text: "A transaction is included in Spendings if it:",
                },
                {
                  type: "list",
                  items: [
                    "is within the selected date range",
                    "is posted (not pending)",
                    "has a positive amount",
                    "is categorized",
                    "is not excluded by spending rules",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "What is not included in spendings",
              children: [
                {
                  type: "paragraph",
                  text: "To keep the total clear and consistent, some transactions are excluded.",
                },
                {
                  type: "paragraph",
                  text: "This includes:",
                },
                {
                  type: "list",
                  items: [
                    "transfers between your own accounts",
                    "most credit card payments",
                    "refunds and reversals",
                    "income transactions",
                  ],
                },
                {
                  type: "paragraph",
                  text: "These are excluded so the number reflects actual spending behavior.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why spendings and budget may look different",
              children: [
                {
                  type: "paragraph",
                  text: "Spendings and budgets are related — but not identical.",
                },
                {
                  type: "paragraph",
                  text: "Spendings apply a global set of exclusion rules.",
                },
                {
                  type: "paragraph",
                  text: "Budgets apply their own filters and qualification rules.",
                },
                {
                  type: "paragraph",
                  text: "Because of this, totals may not match exactly.",
                },
                {
                  type: "paragraph",
                  text: "This is expected.",
                },
              ],
            },
          ],
        },
        {
          id: "s-hc-2",
          title: "Viewing & Comparing Spendings",
          bodyContent: [
            {
              type: "section",
              heading: "How to view your spendings",
              children: [
                {
                  type: "paragraph",
                  text: "Spendings are calculated based on your selected date range.",
                },
                {
                  type: "paragraph",
                  text: "You can then refine your view using:",
                },
                {
                  type: "list",
                  items: [
                    "accounts or institutions",
                    "categories",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This allows you to focus on specific parts of your financial activity.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to compare different periods",
              children: [
                {
                  type: "paragraph",
                  text: "You can change the date range to compare different time periods.",
                },
                {
                  type: "paragraph",
                  text: "For example:",
                },
                {
                  type: "list",
                  items: [
                    "one month vs another",
                    "shorter vs longer periods",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This helps you understand how your spending changes over time.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to group your spendings",
              children: [
                {
                  type: "paragraph",
                  text: "You can group your spendings in two main ways:",
                },
                {
                  type: "list",
                  items: [
                    "by category → to see how spending is distributed",
                    "by time → to see how spending evolves",
                  ],
                },
                {
                  type: "paragraph",
                  text: "When grouping by time, you can choose:",
                },
                {
                  type: "list",
                  items: [
                    "daily view",
                    "monthly view",
                  ],
                },
              ],
            },
          ],
        },
        {
          id: "s-hc-3",
          title: "Understanding Your Spending Patterns",
          bodyContent: [
            {
              type: "section",
              heading: "How spendings are calculated",
              children: [
                {
                  type: "paragraph",
                  text: "Spendings are recalculated every time you change your filters.",
                },
                {
                  type: "paragraph",
                  text: "This means:",
                },
                {
                  type: "list",
                  items: [
                    "totals are recomputed",
                    "breakdowns are updated",
                    "results reflect your current data",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "Why your spendings may change",
              children: [
                {
                  type: "paragraph",
                  text: "Your spendings can change over time because:",
                },
                {
                  type: "list",
                  items: [
                    "new transactions are added",
                    "pending transactions become posted",
                    "transaction categories are updated",
                  ],
                },
                {
                  type: "paragraph",
                  text: "Spendings always use the latest version of your data.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why refunds are not included",
              children: [
                {
                  type: "paragraph",
                  text: "Refunds are excluded from spendings.",
                },
                {
                  type: "paragraph",
                  text: "They are not subtracted from totals — they are ignored.",
                },
                {
                  type: "paragraph",
                  text: "This keeps the spending number consistent and easier to compare across periods.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why pending transactions are not included",
              children: [
                {
                  type: "paragraph",
                  text: "Only posted transactions count.",
                },
                {
                  type: "paragraph",
                  text: "Pending transactions are excluded until they are finalized.",
                },
                {
                  type: "paragraph",
                  text: "This ensures your totals are stable and based on confirmed activity.",
                },
              ],
            },
          ],
        },
        {
          id: "s-hc-4",
          title: "Data Behavior & Expectations",
          bodyContent: [
            {
              type: "section",
              heading: "Why your total may not match your bank",
              children: [
                {
                  type: "paragraph",
                  text: "Your bank may show:",
                },
                {
                  type: "list",
                  items: [
                    "pending transactions",
                    "transfers",
                    "refunds",
                  ],
                },
                {
                  type: "paragraph",
                  text: "Spendings exclude these in certain cases.",
                },
                {
                  type: "paragraph",
                  text: "This is why totals may look different.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why your totals may not match your budget",
              children: [
                {
                  type: "paragraph",
                  text: "Spendings and budgets use different rules.",
                },
                {
                  type: "paragraph",
                  text: "Spendings apply exclusion logic.",
                },
                {
                  type: "paragraph",
                  text: "Budgets apply scope and filtering logic.",
                },
                {
                  type: "paragraph",
                  text: "Because of this, numbers may differ.",
                },
              ],
            },
          ],
        },
      ],
      faq: [
        {
          title: "Why is my spending lower than expected?",
          description:
            "Some transactions may be excluded, such as transfers, refunds, or pending transactions.",
        },
        {
          title: "Why aren’t refunds reducing my spending?",
          description:
            "Refunds are not subtracted — they are excluded from the calculation.",
        },
        {
          title: "Why don’t pending transactions appear?",
          description:
            "Only posted transactions are included. Pending transactions will appear once finalized.",
        },
        {
          title: "Why does my spending change over time?",
          description:
            "Because the system updates as your transaction data changes.",
        },
        {
          title: "Why doesn’t my spending match my budget?",
          description:
            "Spendings and budgets use different calculation rules. This can lead to different totals.",
        },
      ],
    },
  },
  RECURRINGS: {
    title: "Recurring Transactions",
    navigation: [
      {
        path: "help_center",
        text: "Help Center",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
      {
        path: "faq",
        text: "FAQ",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/overview_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/overview_blue.svg`,
      },
    ],
    meta: {
      help_center: { layoutType: "list" },
      faq: { layoutType: "accordion" },
    },
    content: {
      help_center: [
        {
          id: "r-hc-1",
          title: "Understanding Recurring Transactions",
          bodyContent: [
            {
              type: "section",
              heading: "How recurring transactions work in Plaintr",
              children: [
                {
                  type: "paragraph",
                  text: "Recurring transactions are patterns in your financial activity that repeat over time.",
                },
                {
                  type: "paragraph",
                  text: "They include both spending and income.",
                },
                {
                  type: "paragraph",
                  text: "Instead of looking at individual transactions, this view helps you understand what consistently happens in your finances.",
                },
              ],
            },
            {
              type: "section",
              heading: "What counts as a recurring transaction",
              children: [
                {
                  type: "paragraph",
                  text: "A recurring transaction is any transaction that happens regularly.",
                },
                {
                  type: "paragraph",
                  text: "This can include:",
                },
                {
                  type: "list",
                  items: [
                    "subscriptions",
                    "bills",
                    "credit card payments",
                    "payroll or income",
                  ],
                },
                {
                  type: "paragraph",
                  text: "These are identified automatically based on patterns in your transaction history.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why recurring transactions matter",
              children: [
                {
                  type: "paragraph",
                  text: "Recurring transactions show what is predictable in your finances.",
                },
                {
                  type: "paragraph",
                  text: "They help you understand:",
                },
                {
                  type: "list",
                  items: [
                    "fixed or repeating expenses",
                    "expected income",
                    "how consistent these patterns are",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This makes it easier to anticipate what’s coming — not just react to what already happened.",
                },
              ],
            },
            {
              type: "section",
              heading: "How recurring transactions connect to your overall financial view",
              children: [
                {
                  type: "paragraph",
                  text: "Recurring transactions are built from your transaction data.",
                },
                {
                  type: "paragraph",
                  text: "They help provide context for:",
                },
                {
                  type: "list",
                  items: [
                    "your spending patterns",
                    "your budgets",
                    "your financial progress",
                  ],
                },
                {
                  type: "paragraph",
                  text: "They add a forward-looking layer to your financial understanding.",
                },
              ],
            },
          ],
        },
        {
          id: "r-hc-2",
          title: "Viewing Recurring Transactions",
          bodyContent: [
            {
              type: "section",
              heading: "How to view your recurring transactions",
              children: [
                {
                  type: "paragraph",
                  text: "Plaintr displays a list of your recurring transactions.",
                },
                {
                  type: "paragraph",
                  text: "This includes both:",
                },
                {
                  type: "list",
                  items: [
                    "recurring spending",
                    "recurring income",
                  ],
                },
                {
                  type: "paragraph",
                  text: "Each item represents a pattern that has been identified in your activity.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to view the history of a recurring transaction",
              children: [
                {
                  type: "paragraph",
                  text: "Each recurring transaction includes a history of activity.",
                },
                {
                  type: "paragraph",
                  text: "If it’s spending:",
                },
                {
                  type: "list",
                  items: [
                    "you’ll see past payments to that merchant",
                  ],
                },
                {
                  type: "paragraph",
                  text: "If it’s income:",
                },
                {
                  type: "list",
                  items: [
                    "you’ll see past deposits received",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This history is organized by date.",
                },
              ],
            },
            {
              type: "section",
              heading: "How to filter recurring transactions",
              children: [
                {
                  type: "paragraph",
                  text: "You can filter recurring transactions by:",
                },
                {
                  type: "list",
                  items: [
                    "merchant",
                    "account",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This helps you focus on a specific pattern or source.",
                },
              ],
            },
          ],
        },
        {
          id: "r-hc-3",
          title: "Understanding Your Recurring Activity",
          bodyContent: [
            {
              type: "section",
              heading: "How recurring transactions are identified",
              children: [
                {
                  type: "paragraph",
                  text: "Recurring transactions are detected automatically based on patterns in your data.",
                },
                {
                  type: "paragraph",
                  text: "This includes:",
                },
                {
                  type: "list",
                  items: [
                    "frequency",
                    "consistency",
                    "similarity of transactions",
                  ],
                },
                {
                  type: "paragraph",
                  text: "The system groups these into a single recurring item.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why some transactions are marked as recurring",
              children: [
                {
                  type: "paragraph",
                  text: "If a transaction repeats regularly, it may be marked as recurring.",
                },
                {
                  type: "paragraph",
                  text: "This is based on observed behavior — not manual input.",
                },
              ],
            },
            {
              type: "section",
              heading: "Why some recurring transactions may change",
              children: [
                {
                  type: "paragraph",
                  text: "Recurring transactions can evolve over time.",
                },
                {
                  type: "paragraph",
                  text: "For example:",
                },
                {
                  type: "list",
                  items: [
                    "the amount may vary",
                    "the timing may shift",
                    "the pattern may become less consistent",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This reflects real-world changes in your activity.",
                },
              ],
            },
          ],
        },
        {
          id: "r-hc-4",
          title: "Upcoming Recurring Activity",
          bodyContent: [
            {
              type: "section",
              heading: "How to see upcoming transactions",
              children: [
                {
                  type: "paragraph",
                  text: "Plaintr shows a forward-looking view of your recurring transactions.",
                },
                {
                  type: "paragraph",
                  text: "This includes:",
                },
                {
                  type: "list",
                  items: [
                    "expected upcoming charges",
                    "expected incoming payments",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "How the time window works",
              children: [
                {
                  type: "paragraph",
                  text: "You can view upcoming activity within:",
                },
                {
                  type: "list",
                  items: [
                    "the next 7 days (default)",
                    "up to 30 days",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This helps you plan ahead.",
                },
              ],
            },
            {
              type: "section",
              heading: "What “expected” means",
              children: [
                {
                  type: "paragraph",
                  text: "Upcoming transactions are based on past patterns.",
                },
                {
                  type: "paragraph",
                  text: "They represent what is likely to happen — not guaranteed outcomes.",
                },
              ],
            },
          ],
        },
        {
          id: "r-hc-5",
          title: "Managing Recurring Transactions",
          bodyContent: [
            {
              type: "section",
              heading: "How to update a recurring transaction type",
              children: [
                {
                  type: "paragraph",
                  text: "You can update how a recurring transaction is categorized.",
                },
                {
                  type: "paragraph",
                  text: "For example:",
                },
                {
                  type: "list",
                  items: [
                    "bill",
                    "subscription",
                    "credit card payment",
                    "other",
                  ],
                },
                {
                  type: "paragraph",
                  text: "This helps keep your financial view organized.",
                },
              ],
            },
            {
              type: "section",
              heading: "What you can and cannot change",
              children: [
                {
                  type: "paragraph",
                  text: "You can:",
                },
                {
                  type: "list",
                  items: [
                    "update the transaction type",
                    "filter and organize your view",
                  ],
                },
                {
                  type: "paragraph",
                  text: "You cannot:",
                },
                {
                  type: "list",
                  items: [
                    "modify the underlying transaction data",
                    "unsubscribe directly through Plaintr",
                  ],
                },
              ],
            },
            {
              type: "section",
              heading: "About subscriptions and cancellations",
              children: [
                {
                  type: "paragraph",
                  text: "Plaintr helps you identify recurring subscriptions.",
                },
                {
                  type: "paragraph",
                  text: "But it does not cancel or manage them directly.",
                },
                {
                  type: "paragraph",
                  text: "If you want to stop a subscription, you’ll need to do it with the provider.",
                },
              ],
            },
          ],
        },
      ],
      faq: [
        {
          title: "How does Plaintr know something is recurring?",
          description:
            "It identifies patterns based on how often transactions repeat.",
        },
        {
          title: "Why is something marked as recurring when it shouldn’t be?",
          description:
            "Sometimes patterns may appear consistent even if they’re not intentional. You can review and adjust how it’s categorized.",
        },
        {
          title: "Why isn’t a recurring transaction showing up?",
          description:
            "It may not have enough history yet to be identified as recurring.",
        },
        {
          title: "Can I cancel a subscription through Plaintr?",
          description:
            "No. Plaintr helps you identify subscriptions, but you must cancel them with the provider.",
        },
        {
          title: "Why do upcoming transactions sometimes change?",
          description:
            "They are based on past patterns. If your behavior changes, the expected activity may change as well.",
        },
        {
          title: "Can I edit the amount of a recurring transaction?",
          description:
            "No. Amounts reflect real transaction data.",
        },
      ],
    },
  },
};
